import React, { type ReactElement } from "react";

import { faCheckCircle, faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import css from "./LogDetails.module.scss";

type RoleAutoCreateSummaryData = {
	created_names: string[];
	archived_name_collisions: string[];
};

type RoleAutoCreateSummaryProps = {
	roleAutoCreate?: RoleAutoCreateSummaryData | null;
	testingMode?: boolean;
};

/**
 * Sync-log banners for roles the run auto-created and archived names that blocked a create.
 * Renders nothing without role auto-create data (pre-feature logs, uneventful runs). On a
 * testing-mode (dry-run) log the names are the roles the real run WILL create — same data
 * channel, future-tense copy.
 */
export const RoleAutoCreateSummary = ({
	roleAutoCreate,
	testingMode,
}: RoleAutoCreateSummaryProps): ReactElement | null => {
	const createdNames = roleAutoCreate?.created_names ?? [];
	const collisionNames = roleAutoCreate?.archived_name_collisions ?? [];

	if (createdNames.length === 0 && collisionNames.length === 0) {
		return null;
	}

	return (
		<>
			{collisionNames.length > 0 ? (
				<div className={css.warningSummary}>
					<div className={css.summaryHeader}>
						<div className={css.exclamationIcon}>
							<FontAwesomeIcon icon={faExclamationCircle} />
						</div>
						<div>{`${collisionNames.length} role${
							collisionNames.length === 1 ? "" : "s"
						} could not be created.`}</div>
					</div>
					<ul>
						{collisionNames.map((name) => (
							<li key={name} className={css.summaryDetail}>
								{`Role "${name}" was not created because an archived role already uses that name.`}
							</li>
						))}
					</ul>
				</div>
			) : null}

			{createdNames.length > 0 ? (
				<div className={`${css.syncedSummary} ${css.summaryHeader}`}>
					<div className={css.checkIcon}>
						<FontAwesomeIcon icon={faCheckCircle} />
					</div>
					<div>{`${createdNames.length} new role${createdNames.length === 1 ? "" : "s"} ${
						testingMode ? "will be created" : "created"
					}: ${createdNames.join(", ")}.`}</div>
				</div>
			) : null}
		</>
	);
};
